<script setup>
import { computed } from 'vue'

// Tiny CSS mock-ups of a viewer page for the Display option cards. Purely
// illustrative — no assets, just boxes in the dashboard's surface colours.
const props = defineProps({
	kind: {
		type: String,
		required: true,
	},
	value: {
		type: [String, Number, Boolean],
		default: '',
	},
	active: {
		type: Boolean,
		default: false,
	},
})

const accent = computed(() => (props.active ? 'bg-main' : 'bg-surface-6'))
const block = 'bg-surface-5'
const isAuto = computed(() => props.value === '' || props.value === 0)
const cols = computed(() => (typeof props.value === 'number' && props.value > 0 ? props.value : 4))
const cardCount = computed(() => (props.value === true ? 5 : 3))
</script>

<template>
	<div class="relative h-14 w-full overflow-hidden rounded-md bg-surface-2 ring-1 ring-surface-4" aria-hidden="true">
		<!-- Featured banner -->
		<template v-if="kind === 'hero'">
			<div v-if="value === 'immersive'" class="absolute inset-x-0 top-0 h-8 bg-gradient-to-b from-surface-6 to-surface-2">
				<div class="absolute bottom-1.5 left-2 h-1 w-8 rounded-sm bg-white/70" />
				<div class="absolute bottom-1.5 left-11 h-1 w-3 rounded-sm" :class="accent" />
			</div>
			<div v-else class="absolute inset-x-2 top-1.5 h-7 rounded bg-surface-5">
				<div class="absolute bottom-1.5 left-1.5 h-1 w-7 rounded-sm bg-white/70" />
				<div class="absolute bottom-1.5 left-9 h-1 w-3 rounded-sm" :class="accent" />
			</div>
			<div class="absolute inset-x-2 bottom-1.5 grid grid-cols-4 gap-1">
				<div v-for="i in 4" :key="i" class="h-3 rounded-sm" :class="block" />
			</div>
		</template>

		<!-- Catalog vs events -->
		<template v-else-if="kind === 'experience'">
			<div v-if="value === 'events'" class="absolute inset-2 grid grid-cols-2 gap-1.5">
				<div v-for="i in 2" :key="i" class="relative rounded-sm" :class="block">
					<div class="absolute left-1 top-1 h-1 w-4 rounded-sm" :class="accent" />
					<div class="absolute bottom-1 left-1 h-1 w-6 rounded-sm bg-white/60" />
				</div>
			</div>
			<div v-else class="absolute inset-2 grid grid-cols-5 gap-1">
				<div v-for="i in 10" :key="i" class="rounded-sm" :class="block" />
			</div>
		</template>

		<!-- Category presentation -->
		<template v-else-if="kind === 'categories'">
			<template v-if="value === 'rows'">
				<div v-for="r in 2" :key="r" class="absolute inset-x-2" :class="r === 1 ? 'top-1.5' : 'top-8'">
					<div class="mb-1 h-1 w-6 rounded-sm bg-white/60" />
					<div class="grid grid-cols-5 gap-1">
						<div v-for="i in 5" :key="i" class="h-3 rounded-sm" :class="block" />
					</div>
				</div>
			</template>
			<template v-else>
				<div v-if="value === 'dropdown'" class="absolute left-2 top-1.5 flex h-2.5 w-12 items-center justify-between rounded-sm px-1" :class="block">
					<span class="h-0.5 w-5 rounded-sm bg-white/60" /><span class="h-0.5 w-1 rounded-sm bg-white/60" />
				</div>
				<div v-else class="absolute left-2 top-1.5 flex gap-1">
					<span class="h-2.5 w-5 rounded-full" :class="accent" />
					<span v-for="i in 3" :key="i" class="h-2.5 w-4 rounded-full" :class="block" />
				</div>
				<div class="absolute inset-x-2 bottom-1.5 top-5 grid grid-cols-4 gap-1">
					<div v-for="i in 8" :key="i" class="rounded-sm" :class="block" />
				</div>
			</template>
		</template>

		<!-- Poster orientation -->
		<template v-else-if="kind === 'poster'">
			<div class="absolute inset-2 grid gap-1" :class="value === true ? 'grid-cols-5' : 'grid-cols-3'">
				<div v-for="i in cardCount" :key="i" class="rounded-sm" :class="block" />
			</div>
		</template>

		<!-- Card overlays -->
		<template v-else-if="kind === 'overlay'">
			<div v-if="isAuto" class="absolute inset-0 grid place-items-center text-[10px] font-medium text-surface-7">Auto</div>
			<div v-else class="absolute inset-2 grid grid-cols-2 gap-1.5">
				<div v-for="i in 2" :key="i" class="flex flex-col gap-0.5">
					<div class="relative flex-1 rounded-sm" :class="block">
						<div v-if="value === 'full'" class="absolute inset-x-0 bottom-0 h-1/2 rounded-b-sm bg-gradient-to-t from-black/70 to-transparent" />
						<div v-if="value === 'full'" class="absolute bottom-1 left-1 h-1 w-5 rounded-sm bg-white/80" />
						<div v-if="value !== 'none'" class="absolute left-1 top-1 h-1 w-2.5 rounded-sm" :class="accent" />
					</div>
					<div v-if="value !== 'full'" class="h-1 w-5 rounded-sm bg-white/60" />
				</div>
			</div>
		</template>

		<!-- Grid columns -->
		<template v-else-if="kind === 'columns'">
			<div v-if="isAuto" class="absolute inset-0 grid place-items-center text-[10px] font-medium text-surface-7">Auto</div>
			<div v-else class="absolute inset-2 grid gap-1" :style="{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }">
				<div v-for="i in cols * 2" :key="i" class="rounded-sm" :class="block" />
			</div>
		</template>

		<!-- Detail page artwork -->
		<template v-else-if="kind === 'detail'">
			<div v-if="isAuto" class="absolute inset-0 grid place-items-center text-[10px] font-medium text-surface-7">Auto</div>
			<template v-else-if="value === 'contained'">
				<div class="absolute left-2 top-2.5 h-1.5 w-9 rounded-sm bg-white/70" />
				<div class="absolute left-2 top-5 h-1 w-12 rounded-sm bg-white/30" />
				<div class="absolute left-2 top-7 h-1 w-10 rounded-sm bg-white/30" />
				<div class="absolute bottom-2 left-2 h-2 w-6 rounded-sm" :class="accent" />
				<div class="absolute right-2 top-2 h-10 w-16 rounded border border-surface-6" :class="block" />
			</template>
			<template v-else>
				<div class="absolute inset-0 bg-gradient-to-r from-surface-2 via-surface-2/80 to-surface-6" />
				<div class="absolute left-2 top-3 h-1.5 w-9 rounded-sm bg-white/70" />
				<div class="absolute left-2 top-6 h-1 w-12 rounded-sm bg-white/30" />
				<div class="absolute bottom-2 left-2 h-2 w-6 rounded-sm" :class="accent" />
			</template>
		</template>

		<!-- Presets -->
		<template v-else-if="kind === 'preset-cinematic'">
			<div class="absolute inset-x-0 top-0 h-7 bg-gradient-to-b from-surface-6 to-surface-2">
				<div class="absolute bottom-1 left-2 h-1 w-8 rounded-sm bg-white/70" />
			</div>
			<div class="absolute inset-x-2 bottom-1.5">
				<div class="mb-0.5 h-0.5 w-5 rounded-sm bg-white/50" />
				<div class="grid grid-cols-5 gap-1"><div v-for="i in 5" :key="i" class="h-3 rounded-sm" :class="block" /></div>
			</div>
		</template>
		<template v-else-if="kind === 'preset-catalog'">
			<div class="absolute inset-x-2 top-1.5 h-5 rounded bg-surface-5" />
			<div class="absolute left-2 flex gap-1" style="top: 30px">
				<span class="h-1.5 w-4 rounded-full" :class="accent" /><span v-for="i in 3" :key="i" class="h-1.5 w-3 rounded-full" :class="block" />
			</div>
			<div class="absolute inset-x-2 bottom-1.5 grid grid-cols-4 gap-1"><div v-for="i in 4" :key="i" class="h-3 rounded-sm" :class="block" /></div>
		</template>
	</div>
</template>
